import { EirDocumentUploadResponseDto, UpdateOrderEirUrlCommand } from '@planning/app/api'
import {
  NonNumericContainerInfo,
  NumericContainerInfo,
} from '@planning/pages/GateClerk/Components/RoutingSlip'
import { documentsService, nnrOrderService } from '@planning/services'
import { PdfViewerFileProps } from '@tom-ui/ui'
import { generatePdfBlob } from '@tom-ui/utils'
import { action, computed, makeObservable, observable } from 'mobx'

export type PrintoutFileType = 'Eir' | 'RoutingSlip'
export interface PrintoutFileData {
  orderId?: number
  file: File
  blob: Blob
  name: string
  url: string
  type: PrintoutFileType
}

interface PrintoutNnrData {
  id: number
  suggestedContainers?: { containerNumber: string; blockName: string }[]
}

export class PrintoutsViewStore {
  isLoading = false

  documents: PrintoutFileData[] = []

  constructor() {
    makeObservable(this, {
      isLoading: observable,
      documents: observable,

      pdfFileViewerDocuments: computed,

      generatePrintout: action,
      setLoading: action,
      addDocument: action,
      reset: action,
    })
  }

  get pdfFileViewerDocuments(): PdfViewerFileProps[] {
    return this.documents.map(document => ({
      blob: document.blob,
      fileName: document.name,
      url: document.url,
    }))
  }

  async generatePrintout(
    type: PrintoutFileType,
    doc: JSX.Element,
    date: string,
    containerNumber?: string | null,
    orderId?: number,
  ) {
    const blob = await generatePdfBlob(doc)
    this.generateDocument(type, blob, date, containerNumber, orderId)
  }

  async generateDocument(
    type: PrintoutFileType,
    blob: Blob,
    date: string,
    containerNumber?: string | null,
    orderId?: number,
  ) {
    const normalizedPrintDate = date.replaceAll('/', '-')
    const normalizedContainerNumber = containerNumber?.trim()

    const fileName = containerNumber
      ? `${type}_${normalizedContainerNumber}_${normalizedPrintDate}.pdf`
      : `${type}_${normalizedPrintDate}.pdf`

    const file = new File([blob], fileName, {
      type: 'application/pdf',
    })

    const url = URL.createObjectURL(file)

    this.addDocument({
      orderId,
      name: fileName,
      blob,
      file,
      url,
      type,
    })
  }

  async mapNnrOrders(nnrOrders: PrintoutNnrData[]) {
    const nonNumericOrders: (NumericContainerInfo | NonNumericContainerInfo)[] = []

    const nnrOrderAditionalInfos = await Promise.allSettled(
      nnrOrders.map(nnr => nnrOrderService.getById(nnr.id)),
    )

    nnrOrderAditionalInfos.forEach(nnrOrderAdditionalInfoPromise => {
      if (nnrOrderAdditionalInfoPromise.status === 'fulfilled') {
        const nnrOrder = nnrOrders.find(nnr => nnr.id === nnrOrderAdditionalInfoPromise.value.id)
        const nnrOrderAditionalInfo = nnrOrderAdditionalInfoPromise.value

        const location =
          nnrOrder?.suggestedContainers
            ?.map(sc => sc.blockName)
            ?.filter((value, index, self) => self.indexOf(value) === index)
            .join(', ') ?? 'No location provided'

        const suggestedContainers =
          nnrOrder?.suggestedContainers?.map(sc => sc.containerNumber) ?? []

        nonNumericOrders.push({
          type: 'non-numeric',
          location,
          suggestedContainers,
          archetype: nnrOrderAditionalInfo.archetypeName ?? 'Unknown',
          length: nnrOrderAditionalInfo.containerLengths ?? [],
          height: nnrOrderAditionalInfo.containerHeights ?? [],
          containerType: nnrOrderAditionalInfo.containerTypes ?? [],
          referenceNumber: nnrOrderAditionalInfo.referenceNumber,
          operator: nnrOrderAditionalInfo.shippingLine,
        })
      }
    })

    return nonNumericOrders
  }

  addDocument(document: PrintoutFileData) {
    this.documents.push(document)
  }

  async uploadDocuments() {
    const files = this.documents.filter(document => document.type === 'Eir').map(d => d.file)

    if (files.length > 0) {
      const orderDocumentsData = await documentsService.uploadEirDocuments(files)
      await this.updateOrdersDocuments(orderDocumentsData)
    }
  }

  async updateOrdersDocuments(orderDocumentsData: EirDocumentUploadResponseDto[]) {
    const cmd: UpdateOrderEirUrlCommand = {
      orderDocumentUrlData: [],
    }
    orderDocumentsData.forEach(data => {
      const document = this.documents.find(d => d.file.name === data.fileName)

      if (document?.orderId && data.pdfUrl && data.createdAt) {
        cmd.orderDocumentUrlData.push({
          orderId: document?.orderId,
          url: data.pdfUrl,
          createdAt: data.createdAt,
        })
      }
    })

    await documentsService.updateOrderDocumentUrl(cmd)
  }

  setLoading(isLoading: boolean) {
    this.isLoading = isLoading
  }

  reset() {
    this.documents = []
  }
}
